
import Vue from 'vue';
import {
  LabelAssignment,
  LabelPickerAdvancedLabelStatus,
  LabelWithEdges,
  LabelWithStateAndSuggestions,
} from '@/model';
import labels, { findMissingRelationWarning, findRelatedTo, labelSorter } from '@/store/labels';
import LabelWithSuggestionIcons from '@/components/LabelWithSuggestionIcons.vue';

export default Vue.extend({
  name: 'LabelPickerAdvancedSelectedLabels',
  components: { LabelWithSuggestionIcons },

  props: {
    value: { type: Array as () => LabelAssignment[], required: false, default: () => [] },
    isBusy: { type: Boolean, default: false },
    edit: { type: Boolean, default: false },
    labelStatus: {
      type: Object as () => LabelPickerAdvancedLabelStatus,
      required: false,
      default: () => ({
        saved: [],
        added: [],
        removed: [],
        parents: [],
      }) as LabelPickerAdvancedLabelStatus,
    },
  },
  computed: {
    selectedAndAddedLabels(): LabelWithStateAndSuggestions[] {
      return [...this.selectedLabels, ...this.addedLabels];
    },
    selectedLabels(): LabelWithStateAndSuggestions[] {
      return this.value
        .filter((l) => !this.isAddedLabel(l.labelId))
        .map((v) => this.toLabelWithStateAndSuggestions(v))
        .sort(labelSorter(['type', 'length', 'alpha']));
    },
    selectedLabelIds(): number[] {
      return this.value?.map((s) => s.labelId) ?? [];
    },
    addedLabelIds(): number[] {
      return this.labelStatus.added.map((a) => a.labelId);
    },
    addedLabels(): LabelWithStateAndSuggestions[] {
      return this.labelStatus.added.map((a) => this.toLabelWithStateAndSuggestions(a));
    },
    removedLabels(): LabelWithStateAndSuggestions[] {
      return this.labelStatus.removed
        .map((l) => this.toLabelWithStateAndSuggestions(l));
    },
  },
  methods: {
    toLabelWithStateAndSuggestions(labelAssignment: LabelAssignment): LabelWithStateAndSuggestions {
      const parents = this.labelStatus.parents;
      const labelWithEdges = this.toLabelWithEdges(labelAssignment.labelId);
      return ({
        ...labelWithEdges,
        isParentAtIndex: parents
          .find((ls) => ls.id === labelWithEdges.id) ? parents.findIndex((ls) => ls.id === labelWithEdges.id) + 1 : undefined,
        relatedTo: findRelatedTo(labelWithEdges, parents).map((v) => ({
          ...v,
          isRemoved: !!this.labelStatus.removed.find((r) => r.labelId === v.label.id),
        })),
        warning: findMissingRelationWarning(labelWithEdges, parents),
        isAdded: !!this.labelStatus.added.find((a) => a.labelId === labelAssignment.labelId),
        isRemoved: !!this.labelStatus.removed.find((r) => r.labelId === labelAssignment.labelId),
        suggestions: labelAssignment.suggestions ?? [],
      });
    },
    onChipClicked(id: number) {
      const savedLabel = this.labelStatus.saved.find((la) => la.labelId === id);
      const vs = this.value?.concat(savedLabel ?? { labelId: id });
      if (vs) {
        this.$emit('input', vs);
      }
    },
    unselect(labelId: number) {
      const vs = this.value?.filter((la) => la.labelId !== labelId);
      if (vs) {
        this.$emit('input', vs);
      }
    },
    isAddedLabel(id: number): boolean {
      return !!this.addedLabelIds.includes(id);
    },
    toLabelWithEdges(id: number): LabelWithEdges {
      const l = labels.getters.lookupWithEdges.get(id) ?? {
        id,
        code: `unknown label ${id}`,
        type: 'unknown',
        color: { r: 0, b: 0, g: 0 },
        selectable: true,
      };
      return l;
    },
  },
});
